// Central Google Analytics (GA4) tracking.
//
// Every event in the app goes through here so names and parameter shapes stay
// consistent. Safe no-op when gtag isn't present (dev, ad-blockers, SSR).
//
// NAMING — we use GA4's recommended event names wherever the semantics
// genuinely match, because those unlock GA4's prebuilt reports (ecommerce
// funnel, user acquisition, promotions) for free. Where GA4 has no equivalent
// (bookmark removal, push opt-in, PWA install) we keep a clearly-named custom
// event rather than bending a standard name out of shape.
//
// MIGRATION — the five custom names already live in the property are fired
// alongside their new standard names (see LEGACY_ALIASES) so historical
// reports and any saved explorations stay continuous. Once the property has
// ~30 days of data on the new names, set DUAL_FIRE_LEGACY to false and then
// delete the map.

const DUAL_FIRE_LEGACY = true;

// new (standard) name -> old (custom) name shipped before this migration
const LEGACY_ALIASES = {
  select_item: "product_detail_open",
  outbound_click: "view_product_link_click",
  view_promotion: "campaign_impression",
  select_promotion: "campaign_click",
  dismiss_promotion: "campaign_dismiss",
};

// gtag rejects undefined/null params silently and they clutter DebugView, so
// drop them rather than shipping empty keys.
const clean = (params) => {
  const out = {};
  Object.keys(params || {}).forEach((k) => {
    const v = params[k];
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  });
  return out;
};

export const track = (name, params = {}) => {
  if (typeof window === "undefined" || !window.gtag || !name) return;
  const payload = clean(params);
  window.gtag("event", name, payload);
  const legacy = DUAL_FIRE_LEGACY && LEGACY_ALIASES[name];
  if (legacy) window.gtag("event", legacy, payload);
};

// --- Page views -------------------------------------------------------------
// index.html configures gtag with send_page_view:false, so the router is the
// single source of page_view events. Without this a React Router navigation
// records nothing and every session collapses onto its landing page.

// Auth hand-off puts a live session credential in the URL (/?session_id=...).
// That must never reach analytics, so it's stripped from both the path and the
// location before either is sent. UTM params are deliberately kept — campaign
// attribution is the whole point of tagging those links.
const SENSITIVE_PARAMS = ["session_id", "credential", "token", "code"];

const scrubUrl = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    SENSITIVE_PARAMS.forEach((k) => {
      if (u.searchParams.has(k)) u.searchParams.set(k, "redacted");
    });
    u.hash = "";
    return u;
  } catch (_) {
    return null;
  }
};

export const trackPageView = (path, title) => {
  if (typeof window === "undefined" || !window.gtag) return;
  const scrubbedPath = scrubUrl(path);
  const scrubbedHref = scrubUrl(window.location.href);
  window.gtag("event", "page_view", {
    page_path: scrubbedPath ? scrubbedPath.pathname + scrubbedPath.search : path,
    page_location: scrubbedHref ? scrubbedHref.toString() : undefined,
    page_title: title || document.title,
  });
};

// --- Identity ---------------------------------------------------------------
// gtag('set') applies to every configured destination, so we never need to
// repeat the measurement ID here. Height/gender are the two cuts worth
// segmenting this product by; email and name are deliberately NOT sent —
// GA4 forbids PII and we have no need for it.
export const setAnalyticsUser = (user) => {
  if (typeof window === "undefined" || !window.gtag) return;
  if (!user) {
    window.gtag("set", { user_id: undefined });
    window.gtag("set", "user_properties", { logged_in: "false" });
    return;
  }
  // The API returns this as `user_id` (not `id`) — see /api/auth/me.
  window.gtag("set", { user_id: user.user_id || user.id });
  window.gtag("set", "user_properties", {
    logged_in: "true",
    user_height: user.height || undefined,
    user_shoe_size: user.shoe_size || undefined,
    user_gender: user.gender || undefined,
    profile_completed: user.profile_completed ? "true" : "false",
  });
};

// --- Item helpers -----------------------------------------------------------
// One shape for a product everywhere, so view_item_list / select_item /
// view_item / add_to_wishlist all describe the same item identically and GA4
// can stitch them into a funnel.
export const itemFromProduct = (product, extra = {}) => {
  if (!product) return null;
  return clean({
    item_id: product.id,
    item_name: product.product_name,
    item_brand: product.brand || undefined,
    item_category: product.category || undefined,
    item_category2: product.category_id || undefined,
    price: typeof product.price === "number" ? product.price : undefined,
    currency: typeof product.price === "number" ? "INR" : undefined,
    ...extra,
  });
};

// Human-readable name for the list a product was seen or clicked in, so
// view_item_list / select_item can be compared across tabs and filters.
export const listName = ({ tab, height, category, price } = {}) => {
  const parts = [`tab:${tab || "all"}`];
  if (height && height !== "all") parts.push(`height:${height}`);
  if (category && category !== "all") parts.push(`type:${category}`);
  if (price && price !== "all") parts.push(`price:${price}`);
  return parts.join("|");
};
